/** Deutsche Inhalte. Die Modusnamen folgen dem Katalog `de.json`. */
import type { LanguageContent } from '../registry.js';

export const DE: LanguageContent = {
  home: {
    intro: [
      'Retusch ist ein Online-Zeichenspiel für 2 bis 10 Spieler, direkt im Browser, ohne Installation und ohne Konto. Jede Runde gibt dir ein Gemälde, aus dem ein Feld herausgelöscht wurde: male es so nach, dass es im Bild verschwindet. Danach drehen sich die Rollen um, und du musst die Retuschen der anderen aufspüren.',
      'Die meisten Zeichenspiele belohnen den, den man am schnellsten erkennt. Retusch verlangt das Gegenteil: gewinnen heißt, nicht gesehen zu werden.',
    ],
    steps: [
      {
        title: 'Ein Gemälde mit einem Loch',
        text: 'Du bekommst eine Illustration, aus der ein Feld gelöscht wurde.',
        painting: '1_airIsland',
        stage: 'masked',
      },
      {
        title: 'Du malst es nach',
        text: 'Du zeichnest das Feld so, dass es mit dem Bild verschmilzt.',
        painting: '1_airIsland',
        stage: 'painted',
      },
      {
        title: 'Die Jagd',
        text: 'Das Gemälde kommt mit allen Retuschen zurück: finde sie vor den anderen.',
        painting: '1_airIsland',
        stage: 'hunt',
      },
      {
        title: 'Die Auflösung',
        text: 'Jedes Versteck wird aufgedeckt, und die Punkte fallen.',
        painting: '1_airIsland',
        stage: 'reveal',
      },
    ],
    modes: [
      {
        name: 'Gemeinsame Jagd',
        text: 'Alle malen auf demselben Gemälde, jeder in seinem Feld. Dann öffnet sich die Jagd für alle gleichzeitig: eine Retusche wird nur einmal gefunden, von dem, der sie zuerst entdeckt. Das ist der Standardmodus, der, in dem ständig etwas passiert.',
      },
      {
        name: 'Stream',
        text: 'Der Gastgeber malt nicht: er sucht allein, während sich alle anderen in seinem Gemälde verstecken. Für den Livestream gedacht, wo die Jagd zur Show wird. Für immer kostenlos.',
      },
      {
        name: 'Jeder sein Gemälde',
        text: 'Ein Bild pro Spieler. Du suchst nur die Retusche einer einzigen Person, mit einem einzigen Klick. Die einfachste Regel des Spiels, und die nervenaufreibendste.',
      },
      {
        name: 'Nur einer malt',
        text: 'Eine Runde, ein Zeichner, alle anderen hinter ihm her. Die Rolle wandert, und der Zeichner sieht sein eigenes Versteck fallen oder halten.',
      },
      {
        name: 'Battle Royale',
        text: 'Dieselbe Jagd, nur scheidet der Urheber der zuerst entdeckten Retusche aus. Gespielt wird bis zum Letzten, ohne einen einzigen Punkt zu zählen.',
      },
      {
        name: 'Wettbewerb',
        text: 'Niemand sucht. Alle malen dasselbe Feld desselben Gemäldes nach, dann stimmt jeder für die Retusche, die ihm am besten gefällt. Eine Stimme zählt einen Punkt.',
      },
    ],
    faq: [
      {
        question: 'Wie viele Spieler braucht man?',
        answer:
          'Zwei bis zehn. Wenn du allein bist, nimmt ein automatischer Gegner den freien Platz ein, damit die Partie sofort losgeht. Ab vier Spielern entfaltet die gemeinsame Jagd ihre volle Wirkung.',
      },
      {
        question: 'Ist es kostenlos?',
        answer:
          'Ja. Die schnelle Partie, die gemeinsame Jagd und der Stream-Modus sind kostenlos, ohne Zeitlimit und ohne Begrenzung der Partien. Die übrigen Modi sind es vorerst ebenfalls.',
      },
      {
        question: 'Brauche ich ein Konto?',
        answer:
          'Nein. Spitznamen eintippen, klicken, spielen. Ein Konto merkt sich lediglich Spitznamen und Avatar von einer Partie zur nächsten: es bleibt freiwillig.',
      },
      {
        question: 'Funktioniert es auf dem Handy?',
        answer:
          'Ja, die Oberfläche passt sich der Berührung an: eingeklappte Werkzeugleiste, Lupe unter dem Finger, Doppeltippen zum Zoomen. Allerdings malt man mit Maus und großem Bildschirm deutlich genauer.',
      },
      {
        question: 'Wie lade ich meine Freunde ein?',
        answer:
          'Erstelle einen Raum, kopiere den Einladungslink und füge ihn ein, wo du willst. Deine Freunde landen direkt am Tisch, ohne Konto und ohne Installation.',
      },
      {
        question: 'Was ist eine „Retusche“?',
        answer:
          'Das ist das, was du in das gelöschte Feld malst. Gut gemacht, geht sie als Stück des Originals durch und niemand findet sie. Schlecht gemacht, springt sie ins Auge.',
      },
    ],
    headings: {
      intro: 'Ein Zeichenspiel, in dem man verschwinden muss',
      steps: 'So wird gespielt',
      modes: 'Sechs Arten zu spielen',
      faq: 'Häufige Fragen',
      more: 'Mehr erfahren',
    },
    links: {
      rules: 'Die vollständigen Regeln und die Punktevergabe lesen',
      modes: 'Die Modi im Detail vergleichen',
    },
  },

  howToPlay: {
    title: 'Retusch spielen',
    summary:
      'Die vollständigen Regeln von Retusch: der Ablauf einer Runde, die Punktevergabe auf beiden Seiten, die Malwerkzeuge und Tipps zum Verstecken wie zum Suchen.',
    sections: [
      {
        heading: 'Die Idee in drei Sätzen',
        blocks: [
          'Du bekommst ein Gemälde, aus dem ein Feld gelöscht wurde, und malst es so nach, dass es unbemerkt bleibt.',
          'Danach schauen alle auf die retuschierten Gemälde und suchen die übermalten Felder.',
          'Du punktest, indem du schnell findest, und du punktest ebenso lange, wie dein eigenes Versteck hält.',
        ],
      },
      {
        heading: 'Der Ablauf einer Runde',
        blocks: [
          'Eine Partie reiht gleich aufgebaute Runden aneinander, standardmäßig fünf. Jede besteht aus drei Abschnitten.',
          {
            list: [
              'Das Malen. Du bekommst dein Gemälde und dein Feld und hast standardmäßig 2 Minuten, um es zu übermalen. Die Schaltfläche Fertig gilt als Abgabe: sobald alle abgegeben haben, geht die Runde weiter, ohne auf die Uhr zu warten.',
              'Die Jagd. Das Gemälde erscheint wieder, mit den Retuschen, und du hast standardmäßig 30 Sekunden, um sie zu entdecken. Ein Klick auf ein übermaltes Feld zeigt es dem ganzen Tisch.',
              'Die Auflösung. Alle Verstecke werden umrahmt und benannt, auch die, die niemand gesehen hat. Ein Regler erlaubt es, das retuschierte Gemälde mit dem Original zu vergleichen.',
            ],
          },
        ],
      },
      {
        heading: 'Die Punktevergabe',
        blocks: [
          'Zwei Zähler laufen gleichzeitig, und sie arbeiten gegeneinander: wer sucht, will schnell sein, wer gemalt hat, will, dass sein Feld hält.',
          {
            list: [
              'Als Suchender: 1000 Punkte, wenn du innerhalb der ersten 3 Sekunden findest, danach ein gleichmäßiges Absinken bis auf 100 Punkte am Ende der Phase. Danebentippen oder nichts finden bringt nichts.',
              'Als Zeichner: nur 100 Punkte, wenn dein Feld sofort fällt, danach ein gleichmäßiger Anstieg bis auf 800 Punkte, wenn es bis zum Ende hält. Eine Retusche, die niemand findet, ist das Maximum wert, 800 Punkte.',
            ],
          },
          'Der Zeichner wird nur einmal bezahlt, bei der Auflösung, und zwar nach dem schnellsten Sucher. Es macht also keinen Unterschied, ob zwei oder acht Leute dich suchen: es zählt nur, wer dich zuerst findet.',
          'Die Punktevergabe bleibt gleich, welche Dauer du für die Phase auch einstellst. Eine längere Jagd verschiebt die beiden Grenzwerte nicht, nur die Steigung dazwischen.',
          'Ein einziger Fehler pro Runde. Der erste Klick, der keine Retusche trifft, beendet deine Jagd: ohne diese Regel würde man das Bild wahllos absuchen, bis man zufällig darauf stößt. Zwei Klicks kosten nichts: auf das eigene Feld, und auf eine bereits gefundene Retusche.',
        ],
      },
      {
        heading: 'Die Malwerkzeuge',
        blocks: [
          {
            list: [
              'Der Pinsel (B): drei schnelle Größen, dazu ein Regler für die genaue Größe. Die Kürzel 1, 2 und 3 wählen die drei Größen.',
              'Der Farbeimer (P): füllt eine einfarbige Fläche auf einen Schlag, ohne je über dein Feld hinauszulaufen.',
              'Die Pipette (I): nimmt eine Farbe an jeder Stelle des Gemäldes auf. Alt gedrückt halten wechselt für einen Klick dorthin und gibt danach das vorige Werkzeug zurück.',
              'Die Hand (H): verschiebt die Ansicht. Außerhalb deines Feldes tun Pinsel und Eimer dasselbe, du musst also fast nie das Werkzeug wechseln.',
              'Rückgängig (Strg+Z oder Cmd+Z): geht Strich für Strich zurück, bis zu dreißigmal. Ein Wiederherstellen gibt es nicht.',
              'Das Hintergrundfeld am Ende der Palette: es ist die Startfarbe deines Feldes. Damit zu malen entspricht dem Löschen, nur ist es ein Strich wie jeder andere, der sich rückgängig machen und übermalen lässt. Deshalb gibt es keinen Radiergummi.',
            ],
          },
        ],
      },
      {
        heading: 'Fünf Tipps zum Verstecken',
        blocks: [
          {
            list: [
              'Farben aufnehmen, nicht raten. Die Pipette liefert den genauen Farbton der Umgebung: eine leicht danebenliegende Fläche erkennt man von weitem, eine exakte wird unsichtbar.',
              'Setze fort, was durch dein Feld läuft. Eine Mauer, ein Horizont, ein Ast, der auf der einen Seite hineinläuft, muss auf der anderen an der richtigen Stelle wieder herauskommen. Eine unterbrochene Linie ist der erste Hinweis, nach dem erfahrene Spieler suchen.',
              'Male zuerst die Ecken. Das ist der häufigste Fehler: die Mitte ist sauber, die vier Ecken bleiben im ursprünglichen Hintergrund, und das Quadrat sticht mit bloßem Auge hervor, ohne dass man die Zeichnung überhaupt ansehen müsste.',
              'Arbeite vom Groben ins Feine. Setze zuerst mit einem dicken Pinsel die großen Farbflächen, dann verfeinere. Umgekehrt frisst eine Ecke deine ganze Zeit, während der Rest leer bleibt.',
              'Übernimm den Detailgrad der Umgebung. Ein zu sauberes Feld inmitten einer belebten Zone fällt genauso auf wie ein schludriges inmitten einer glatten Fläche.',
            ],
          },
        ],
      },
      {
        heading: 'Vier Tipps zum Suchen',
        blocks: [
          {
            list: [
              'Achte auf Linien, die aufhören. Eine Kante, ein Kabel, eine Tischkante, die um ein Haar verspringt: dort ist es fast immer.',
              'Erst überfliegen, dann zoomen. In der Gesamtansicht wirkt ein schlecht eingefügtes Feld wie ein Fleck. Der Zoom dient dem Bestätigen, nicht dem Suchen.',
              'Misstraue dem allzu Glatten. Eine vollkommen gleichmäßige Fläche inmitten einer Textur ist genauso verdächtig wie ein Schmierer.',
              'Klicke nicht aufs Geratewohl. Du hast nur einen Fehler frei: drei Sekunden Prüfen kosten weniger als die ganze Runde.',
            ],
          },
        ],
      },
      {
        heading: 'Und je nach Modus?',
        blocks: [
          'Alles bisher Gesagte beschreibt die gemeinsame Jagd, den Standardmodus. Die anderen Modi ändern, wer malt, wer sucht und wie man gewinnt: die Einzelheiten stehen auf der Seite der Spielmodi.',
        ],
      },
    ],
  },

  modes: {
    title: 'Die Spielmodi von Retusch',
    summary:
      'Die sechs Arten, Retusch zu spielen: gemeinsame Jagd, Stream, jeder sein Gemälde, nur einer malt, Battle Royale und Wettbewerb. Ablauf, empfohlene Spielerzahl und Dauer.',
    sections: [
      {
        heading: 'Gemeinsame Jagd',
        blocks: [
          'Der Standardmodus, und der, mit dem man anfangen sollte.',
          'Alle malen auf demselben Gemälde, jeder in seinem eigenen Feld. Sobald die Retuschen abgegeben sind, kommt das Bild vollständig zurück und die Jagd öffnet sich für alle gleichzeitig. Eine Retusche wird nur einmal gefunden, von dem, der sie zuerst entdeckt: sie erscheint dann für den ganzen Tisch und ist aus dem Spiel. Die eigene findet man nie, und ein Versteck, das niemand sieht, bringt seinem Urheber das Maximum.',
          'Ideal für eine große Gruppe: es gibt keine Leerlaufzeiten, alle suchen gleichzeitig, und jeder Fund erscheint auf allen Bildschirmen.',
          'Empfohlen: 3 bis 10 Spieler.',
        ],
      },
      {
        heading: 'Stream',
        blocks: [
          'Das Spiegelbild von „nur einer malt“, für den Livestream gedacht.',
          'Alle Spieler malen auf demselben Gemälde, außer dem Gastgeber. Danach ist der Gastgeber der Einzige, der sucht, während sein Publikum ihm dabei zusieht. Er sammelt keine Punkte und taucht in der Wertung nicht auf: gespielt wird zwischen denen, die sich verstecken, er führt durch die Jagd. Er darf sich so oft irren, wie er will, die Regel des einen Fehlers gilt für ihn nicht.',
          'Ideal für einen Livestream, und zwar aus einem bestimmten Grund: weil der Gastgeber nicht malt, sieht das Publikum sein Versteck nie entstehen. In den anderen Modi schaut es ihm beim Ausmalen seines Feldes zu, und die Jagd hat keine Überraschung mehr.',
          'Empfohlen: 4 bis 10 Spieler. Kostenlos, und das bleibt so.',
        ],
      },
      {
        heading: 'Jeder sein Gemälde',
        blocks: [
          'Jeder Spieler bekommt sein eigenes Bild und versteckt darin seine Retusche. Bei der Jagd bekommst du das Gemälde einer einzigen anderen Person, und du hast einen einzigen Klick, um auf das übermalte Feld zu zeigen. Kein Wettlauf, keine Konkurrenz: nur du, ein Bild und eine Entscheidung.',
          'Ideal zu zweit, oder wenn man eine ruhige Partie will, bei der jeder sich Zeit nimmt.',
          'Empfohlen: 2 bis 10 Spieler.',
        ],
      },
      {
        heading: 'Nur einer malt',
        blocks: [
          'Eine Runde, ein Zeichner, alle anderen hinter ihm her.',
          'In jeder Runde bekommt eine einzige Person ein Gemälde. Die anderen warten und suchen dann ihre Retusche, jeder nach seiner eigenen Zeit bewertet. Der Zeichner wird nach dem schnellsten Sucher bewertet und sieht sein eingefrorenes Versteck, während danach gesucht wird. Die Rolle wandert: die Rundeneinstellung zählt hier volle Durchgänge, denn eine Partie ist nur dann fair, wenn jeder gleich oft gemalt hat.',
          'Ideal für eine kleine Gruppe, die sich unter gleichen Bedingungen messen will, eine Zeichnung nach der anderen.',
          'Empfohlen: 3 bis 6 Spieler, darüber wird das Warten lang.',
        ],
      },
      {
        heading: 'Battle Royale',
        blocks: [
          'Die gemeinsame Jagd, mit einem Ausscheiden pro Runde.',
          'Dasselbe geteilte Gemälde, dieselbe gleichzeitige Jagd. Nur werden hier keine Punkte gezählt: die einzige Frage ist, wer bleibt. Der Spieler, dessen Retusche zuerst entdeckt wird, verlässt die Partie. Die anderen suchen bis zum Ende der Runde weiter, was niemanden mehr ausscheiden lässt, aber jedem seinen Platz gibt, angezeigt auf seinem Feld. Wird niemand gefunden, scheidet auch niemand aus.',
          'Ideal zum Abschluss eines Abends: die Spannung steigt, während sich der Tisch leert, und die letzte Runde ist ein Duell.',
          'Empfohlen: 4 bis 10 Spieler.',
        ],
      },
      {
        heading: 'Wettbewerb',
        blocks: [
          'Der einzige Modus, in dem niemand sucht.',
          'Alle malen dasselbe Feld desselben Gemäldes nach. Danach geht jeder in seinem Tempo die Retuschen der anderen durch und stimmt für die, die er am gelungensten findet. Eine erhaltene Stimme bringt ihrem Urheber einen Punkt. Während der Abstimmung sind die Urheber anonym, und für sich selbst darf man nicht stimmen: Namen und Stimmen kommen erst bei der Auflösung heraus.',
          'Ideal, wenn die Gruppe gemischt ist, oder wenn einige lieber malen wollen, ohne den Druck der Jagduhr.',
          'Empfohlen: 3 bis 10 Spieler.',
        ],
      },
    ],
  },
  modeTable: [
    { name: 'Gemeinsame Jagd', players: '3 bis 10', duration: '15 bis 20 Min.' },
    { name: 'Stream', players: '4 bis 10', duration: '15 bis 20 Min.' },
    { name: 'Jeder sein Gemälde', players: '2 bis 10', duration: '15 bis 20 Min.' },
    { name: 'Nur einer malt', players: '3 bis 6', duration: '20 bis 35 Min.' },
    { name: 'Battle Royale', players: '4 bis 10', duration: '20 bis 30 Min.' },
    { name: 'Wettbewerb', players: '3 bis 10', duration: '10 bis 15 Min.' },
  ],
  modeTableHeadings: ['Modus', 'Spieler', 'Dauer'],
  premium: {
    heading: 'Kostenlose und kostenpflichtige Modi',
    blocks: [
      'Die gemeinsame Jagd und der Stream-Modus sind dauerhaft kostenlos.',
      'Die vier anderen werden beim ERSTELLEN einer Partie kostenpflichtig, und nur dann. Das Beitreten bleibt immer kostenlos: es genügt, dass eine Person am Tisch das Spiel freigeschaltet hat, damit alle jeden Modus spielen können, ohne Konto und ohne zu zahlen.',
      'Die Freischaltung ist ein einmaliger Kauf, ohne Abo, und sie entfernt außerdem die Werbung für das Konto, das sie getätigt hat. Bis dahin stehen alle Modi allen offen.',
    ],
  },

  blog: [
    {
      slug: 'quest-ce-que-retusch',
      title: 'Was ist Retusch?',
      date: '2026-08-21',
      summary:
        'Ein Online-Zeichenspiel für mehrere, in der Form nahe an Gartic Phone, dessen Mechanik aber eher an Meccha Chameleon erinnert: statt erkannt zu werden, will man verschwinden.',
      sections: [
        {
          heading: 'Ein Zeichenspiel, in dem Erkanntwerden verliert',
          blocks: [
            'Retusch ist ein Online-Zeichenspiel für 2 bis 10 Spieler. Jede Runde gibt dir eine Illustration, aus der ein Feld gelöscht wurde, und du musst es so nachmalen, dass es mit dem Bild verschmilzt. Danach schauen alle auf die retuschierten Gemälde und suchen die übermalten Felder.',
            'Was es unterscheidet, passt in einen Satz: gewinnen heißt, nicht gesehen zu werden.',
          ],
        },
        {
          heading: 'Was es mit Gartic Phone teilt',
          blocks: [
            'Die Form ist dieselbe: Link öffnen, Spitznamen eintippen, spielen. Kein Konto, keine Installation, kein Client zum Herunterladen. Das Spiel läuft im Browser, eine Partie startet in Sekunden. Freunde lädt man ein, indem man einen Link in einen Chat einfügt, und der Tisch füllt sich.',
            'Auch der Rhythmus kommt bekannt vor: kurze Runden, eine Uhr, eine Malphase und dann eine Phase, in der man sieht, was die anderen zustande gebracht haben. Und vor allem diese gemeinsame Auflösung am Rundenende, bei der alle kommentieren, was sie sehen. Es ist dasselbe Gruppenvergnügen.',
            'Wie bei Gartic Phone muss man nicht zeichnen können. Ein gut gesetztes Stück Himmel schlägt ein Meisterwerk, das über den Rand läuft.',
          ],
        },
        {
          heading: 'Was es Meccha Chameleon entlehnt',
          blocks: [
            'Im Kern liegt die Mechanik allerdings woanders. In Meccha Chameleon, einem dreidimensionalen Spiel auf Steam, bemalt man seine eigene Figur und stellt sie dann in eine Szene, in der Hoffnung, dass niemand sie von ihrer Umgebung unterscheiden kann. Der Sieg kommt aus der Ähnlichkeit und der guten Platzierung, nicht aus dem Handeln.',
            'Retusch greift diese Idee auf und holt sie zurück ins Bild. Du wählst nicht, wo du dich versteckst, das Feld gibt dir das Spiel vor; dafür fertigst du vollständig an, was hineinkommt. Das Versteck gelingt, wenn der Blick darüber hinweggleitet, ohne hängen zu bleiben.',
            'Und wie in jedem Tarnspiel entsteht die Spannung aus der Doppelrolle. Du bist gleichzeitig versteckt und Jäger: während du das Gemälde nach einer verdächtigen Fläche absuchst, sucht jemand deines ab.',
          ],
        },
        {
          heading: 'Was es sonst nirgends gibt',
          blocks: [
            'Die Punktevergabe belohnt beide Seiten gegenläufig. Wer sucht, punktet umso mehr, je schneller er findet; wer gemalt hat, punktet umso mehr, je länger sein Feld standhält. Eine Retusche, die niemand sieht, bringt ihrem Urheber das Maximum.',
            'Daraus ergibt sich eine sehr eigene Spielweise: es geht nicht darum, schön zu malen, sondern richtig. Den passenden Farbton aufnehmen, eine Linie an der richtigen Stelle fortsetzen, keine Ecke leer lassen. Das sind Handlungen des Beobachtens, bevor es Handlungen des Zeichnens sind.',
            'Am einfachsten versteht man es in der Praxis: die vollständigen Regeln lesen, oder eine schnelle Partie starten, die ohne Einstellungen und ohne Warten losgeht.',
          ],
        },
      ],
    },
    {
      slug: 'comment-etre-imbattable',
      title: 'Wie man bei Retusch unschlagbar wird',
      date: '2026-08-21',
      summary:
        'Tastenkürzel, die Arbeitsweise vom Groben ins Feine und der Fehler mit den leeren Ecken: drei Dinge, die einen Anfänger von einem Spieler trennen, den man nicht mehr findet.',
      sections: [
        {
          heading: 'Lerne die Kürzel, und du verdoppelst deine nutzbare Zeit',
          blocks: [
            'Zwei Minuten Malzeit vergehen sehr schnell, wenn man jedes Werkzeug mit der Maus holt. Kürzel holen leicht zwanzig bis dreißig Sekunden pro Runde zurück, und genau diese Sekunden steckst du später in die Details.',
            {
              list: [
                'B für den Pinsel, P für den Farbeimer, I für die Pipette, H für die Hand.',
                '1, 2 und 3 für die drei Pinselgrößen, ohne den Umweg über die Werkzeugleiste.',
                'Strg+Z, auf dem Mac Cmd+Z, um Strich für Strich rückgängig zu machen.',
                'Alt gedrückt halten wechselt zur Pipette und gibt das vorige Werkzeug zurück, sobald du loslässt. Das ist das nützlichste von allen: du nimmst mitten im Zug eine Farbe auf, ohne je das Werkzeug zu wechseln.',
              ],
            },
            'Ein Hinweis zu AZERTY-Tastaturen: die Tasten 1, 2 und 3 funktionieren ohne Umschalttaste, auch wenn dort &, é und " steht. Das Spiel liest die Position der Taste, nicht das Zeichen, das sie erzeugt.',
          ],
        },
        {
          heading: 'Male vom Groben ins Feine',
          blocks: [
            'Der klassische Anfängerfehler ist, mit einer Ecke anzufangen und sie auszufeilen. Die Uhr läuft ab, drei Viertel des Feldes sind noch Hintergrund, und die Retusche sieht man aus zehn Metern.',
            'Richtig ist der umgekehrte Weg. Fang mit einem dicken Pinsel an, oder mit dem Farbeimer, und setze die großen Farbflächen: den Himmel, die Wand, den Schatten am Boden. Nach zwanzig Sekunden hat dein Feld bereits die richtige Gesamtwirkung und liest sich nicht mehr als Fremdkörper.',
            'Erst danach verkleinerst du und kümmerst dich um die Übergänge: die durchlaufende Horizontlinie, die Kante eines Gegenstands, den Farbwechsel auf halber Höhe. Fehlt die Zeit, hast du Details verloren, nicht die Runde.',
            'Nimm deine Farben grundsätzlich mit der Pipette auf, statt sie in der Palette auszuwählen. Ein ungefährer Ton reicht, um ein ganzes Feld zu verraten, während der exakte Ton es unsichtbar macht.',
          ],
        },
        {
          heading: 'Lass die Ecken niemals leer',
          blocks: [
            'Das ist Hinweis Nummer eins, und das Erste, wonach erfahrene Spieler suchen. Ein Feld mit gut behandelter Mitte, dessen vier Ecken aber in der Hintergrundfarbe geblieben sind, zeichnet ein perfektes Rechteck auf den Bildschirm. Man muss die Zeichnung nicht einmal ansehen: die geometrische Form springt ins Auge.',
            'Gewöhne dir an, zuerst den Rand zu behandeln und dann die Mitte. Ein gut eingefügter Rahmen verzeiht ein grobes Inneres; umgekehrt gilt das nie.',
            'Male absichtlich über die Ränder hinaus. Das Spiel beschneidet deinen Strich an der Grenze des Feldes, du riskierst also nichts, und es ist der einzige Weg, sicherzugehen, dass in keiner Ecke ein Pixel Hintergrund überlebt.',
          ],
        },
        {
          heading: 'Und wenn du an der Reihe bist zu suchen',
          blocks: [
            'Überfliege zuerst das ganze Gemälde ohne Zoom: ein schlecht eingefügtes Feld wirkt wie ein Fleck, und der Zoom dient nur der Bestätigung. Folge danach den langen Linien, denen, die das Bild durchqueren: eine um ein Haar verschobene Kante ist fast immer eine Retusche.',
            'Und denk daran, dass du pro Runde nur einen Fehler frei hast. Drei Sekunden Prüfen kosten weniger als die ganze Runde.',
          ],
        },
      ],
    },
    {
      slug: 'jeu-de-dessin-entre-amis',
      title: 'Ein Zeichenspiel für den Abend mit Freunden',
      date: '2026-08-21',
      summary:
        'Warum Retusch in kleiner Runde gut funktioniert, wie man per Link einlädt, und warum ein einziger Spieler genügt, um die kostenpflichtigen Modi für den ganzen Tisch freizuschalten.',
      sections: [
        {
          heading: 'Ein Spiel, das von niemandem etwas verlangt',
          blocks: [
            'Das größte Hindernis eines Spieleabends auf Distanz ist die Einrichtung. Ein Konto anlegen, einen Client herunterladen, eine Version, die nicht passt: die halbe Gruppe steigt aus, bevor gespielt wird.',
            'Retusch läuft im Browser. Deine Freunde klicken auf einen Link, tippen einen Spitznamen ein und sitzen am Tisch. Es gibt nichts zu installieren, nichts anzulegen, und es funktioniert auf dem Rechner ebenso gut wie auf dem Handy.',
            'Noch etwas zählt in einer Gruppe: niemand bleibt außen vor, weil er nicht zeichnen kann. Das Spiel verlangt nicht, etwas darzustellen, sondern nur, ein Feld an seine Umgebung anzugleichen. Wer schlecht zeichnet, aber gut beobachtet, gewinnt oft.',
          ],
        },
        {
          heading: 'Einladen, praktisch gesehen',
          blocks: [
            'Erstelle einen Raum auf der Startseite. Nimm den Haken bei „öffentlicher Raum“ weg, wenn ihr unter euch bleiben wollt: die Einladung per Link ist dann der einzige Zugang.',
            'Kopiere den Link mit der vorgesehenen Schaltfläche und füge ihn in euren Gruppenchat, euren Sprachkanal oder euren Messenger ein. Er enthält den Raumcode: deine Freunde landen direkt an deinem Tisch.',
            'Du kannst die Partie einstellen, während die Leute eintrudeln: Modus, Rundenzahl, Malzeit, Feldgröße und sogar die Bildkategorien. Nichts ist festgelegt, bevor du startest.',
          ],
        },
        {
          heading: 'Einer zahlt, alle spielen',
          blocks: [
            'Zwei Modi sind dauerhaft kostenlos: die gemeinsame Jagd, der Standardmodus, und der Stream-Modus.',
            'Die übrigen werden kostenpflichtig, aber nur beim ERSTELLEN einer Partie. Das Beitreten bleibt kostenlos, ohne Bedingungen. Praktisch heißt das: es genügt, dass eine einzige Person der Gruppe das Spiel freigeschaltet hat, damit der ganze Tisch alle Modi nutzen kann, ohne Konto und ohne zu zahlen.',
            'Die Freischaltung ist ein einmaliger Kauf, ohne Abo.',
          ],
        },
        {
          heading: 'Welcher Modus für welche Gruppe',
          blocks: [
            'Zu dritt oder viert genügt die gemeinsame Jagd, und sie ist nach einer Runde verstanden. Ab sechs Spielern wird sie richtig gut: die Funde folgen dicht aufeinander und auf dem Bildschirm ist ständig Bewegung.',
            'Zum Abschluss eines Abends probiert Battle Royale: der Tisch leert sich Runde um Runde, und die letzte ist ein Duell. Für eine gemischte Gruppe, in der manche lieber in Ruhe malen, nimmt der Wettbewerb den ganzen Druck der Jagduhr heraus.',
          ],
        },
      ],
    },
    {
      slug: 'jeu-de-dessin-en-stream',
      title: 'Ein Zeichenspiel für den Livestream',
      date: '2026-08-21',
      summary:
        'Retusch hat einen Modus für den Livestream, in dem der Streamer allein sucht, während sein Publikum sich versteckt. Wie man ihn nutzt, und welche anderen Modi vor Publikum funktionieren.',
      sections: [
        {
          heading: 'Ein Modus, der genau dafür gemacht ist',
          blocks: [
            'Der Stream-Modus dreht die Rollen um. Alle Spieler malen auf demselben Gemälde, außer dem Gastgeber, der gar nicht malt. Danach ist der Gastgeber der Einzige, der sucht, während sein Publikum zuschaut.',
            'Genau das braucht ein Livestream, und zwar aus einem bestimmten Grund: weil der Gastgeber nicht malt, existiert sein eigenes Versteck nicht, und der Chat hat nichts gesehen, was er nicht sehen sollte. Die Verstecke der anderen Spieler kennt das Publikum dagegen sehr wohl, und es kann zusehen, wie der Gastgeber dreimal hintereinander daran vorbeiklickt.',
            'Zwei Regeln wurden für diesen Fall geschrieben. Der Streamer sammelt keine Punkte und taucht in der Wertung nicht auf: gespielt wird zwischen denen, die sich verstecken, er führt durch die Show. Und er darf sich so oft irren, wie er will, während alle anderen Modi beim ersten Fehler enden. Ein Livestream kann nicht mit einem unglücklichen Klick zu Ende gehen.',
            'Der Stream-Modus ist kostenlos, und das bleibt so.',
          ],
        },
        {
          heading: 'Der Wettbewerb, die zweite gute Idee',
          blocks: [
            'Im Wettbewerbsmodus malen alle dasselbe Feld desselben Gemäldes nach, danach stimmt jeder für die Retusche, die ihm am besten gefällt. Es gibt nichts mehr zu finden, nur noch zu beurteilen.',
            'Vor Publikum funktioniert das sehr gut, und zwar aus demselben Grund wie der Stream-Modus: es gibt nichts zu finden, also nichts, was der Chat verraten könnte. Die Beiträge lassen sich nebeneinander vergleichen, die Abstimmung liefert einen Moment Spannung, und die Auflösung der Namen einen sauberen Schluss. Es ist außerdem der kürzeste Modus.',
          ],
        },
        {
          heading: 'Und die anderen Modi',
          blocks: [
            'Nichts zwingt zu einem eigens gedachten Modus, aber dann spielt man besser mit Freunden als mit dem Chat. In allen anderen Modi malt der Streamer sein eigenes Feld auf dem Bildschirm: das Publikum sieht es entstehen und kennt die Antwort, bevor die Jagd überhaupt beginnt.',
            'Eine Partie unter Freunden lässt sich dagegen sehr gut von außen verfolgen: die Malphase zeigt, wie jeder sein Versteck baut, und die Auflösung deckt alle Felder auf einmal auf, mit dem Namen des Urhebers.',
          ],
        },
        {
          heading: 'Den Chat ins Spiel holen',
          blocks: [
            'Der Einladungslink ist das Einzige, was geteilt werden muss. Füge ihn in den Chat ein, und die Ersten füllen den Tisch: kein Konto anzulegen, kein Download, keine Warteschlange.',
            'Die Obergrenze stellst du im Raum ein, zwischen 2 und 10 Spielern. Bei großem Publikum öffne lieber mehrere kurze Partien als eine lange: eine Runde dauert unter drei Minuten.',
          ],
        },
      ],
    },
    {
      slug: 'nouveautes',
      title: 'Neues in Retusch',
      date: '2026-08-21',
      summary:
        'Die Änderungen am Spiel, von der neuesten zur ältesten: neue Modi, neue Regeln, Verbesserungen beim Malen und beim Suchen.',
      sections: [
        {
          heading: 'August 2026',
          blocks: [
            {
              list: [
                'Das zu übermalende Feld beginnt nicht mehr als weißes Quadrat, sondern in der vorherrschenden Farbe dessen, was es verdeckt. Man verliert nicht mehr die halbe Zeit damit, einen Hintergrund zu übermalen, den es im Gemälde gar nicht gibt.',
                'Der Farbeimer hinterlässt keinen hellen Saum mehr entlang der Striche: er schiebt sich nun unter deren weichen Rand.',
                'Battle Royale zählt keine Punkte mehr. Der zuerst entdeckte Spieler verlässt die Partie, und jeder bekommt einen Rundenplatz, angezeigt auf seinem Feld, von Rot bis Grün.',
                'Der Stream-Modus lässt dem Streamer so viele Versuche, wie er will: sein erster Fehlgriff beendet die Runde nicht mehr.',
                'Im Zuschauermodus kann man das Gemälde, dessen Entstehung man verfolgt, jetzt verschieben und heranzoomen.',
                'Emoji-Reaktionen starten am Spitznamen ihres Urhebers in der Spielerliste, statt vom unteren Bildschirmrand aufzusteigen.',
                'Außerhalb des zu übermalenden Feldes verschieben Pinsel und Eimer die Ansicht: kein Wechsel zum Handwerkzeug mehr nötig.',
                'Der Spielname oben links führt während einer Partie nach Bestätigung zurück zur Startseite.',
                'Japanisch und Koreanisch kommen zu den fünfzehn bereits verfügbaren Sprachen hinzu.',
              ],
            },
          ],
        },
        {
          heading: 'Juli 2026',
          blocks: [
            {
              list: [
                'Neuer Wettbewerbsmodus: alle malen dasselbe Feld nach, danach stimmt jeder für die Retusche, die ihm am besten gefällt.',
                'Neuer Stream-Modus für den Livestream: der Gastgeber sucht allein, während sein Publikum sich versteckt.',
                'Bildkategorien ersetzen die Schwierigkeitsgrade. Man hakt an, was man sehen will, und jede Kategorie zeigt ein Beispielgemälde.',
                'Zwei Runden hintereinander landen nicht mehr auf demselben Gemälde, und die angehakten Kategorien wechseln sich von Runde zu Runde ab.',
                'In der Galerie am Partieende lässt sich eine Retusche Strich für Strich noch einmal abspielen und als Bild oder als animiertes GIF speichern.',
              ],
            },
          ],
        },
      ],
    },
  ],

  ui: {
    atAGlance: 'Auf einen Blick',
    allArticles: 'Alle Artikel',
    theRules: 'Die Spielregeln',
    theModes: 'Die Spielmodi',
  },
};
